import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';

// All five screens come from one source image laid out side by side.
// Each screen is cropped from it using coordinates in the image's own space.
const SCREENS_IMAGE = 'https://cdn.magicpatterns.com/uploads/mSU8YMGMpkBrBrpmN6i5Yd/Screenshot_2026-10-02_at_09.48.34.png';
const SOURCE_WIDTH = 1830;
const SOURCE_HEIGHT = 640;
const CROP_WIDTH = 306;
const CROP_HEIGHT = 612;
const PROOF_SCREENS = [{
  x: 17,
  y: 5,
  alt: 'Long-term relief story screen with patient video',
  label: 'Story',
  offset: 'translate-y-8'
}, {
  x: 388,
  y: 9,
  alt: 'Head and neck efficacy proof screen showing 87%',
  label: 'Proof',
  offset: '-translate-y-4'
}, {
  x: 755,
  y: 8,
  alt: 'Interactive patient pictogram screen showing 48.2%',
  label: 'Interact',
  offset: 'translate-y-5'
}, {
  x: 1124,
  y: 12,
  alt: 'Comparative efficacy and side-effect bar chart screen',
  label: 'Compare',
  offset: '-translate-y-6'
}, {
  x: 1495,
  y: 12,
  alt: 'Closing question screen with next-step actions',
  label: 'Action',
  offset: 'translate-y-3'
}] as const;
const PAYOFFS = ['Short enough to finish.', 'Interesting enough to continue.', 'Interactive enough to learn from.'] as const;
export function SlideSnakDefinition({}: SlideProps) {
  return <InteriorSlide surface data-id="element-381" data-mp="202noqbl1fwrr">
      <div className="flex h-full min-h-0 flex-col lg:grid lg:grid-cols-12 lg:items-center lg:gap-12 xl:gap-16" data-id="element-382" data-mp="201uev1qs8r5e">
        <div className="flex min-h-0 flex-col justify-center lg:col-span-5" data-id="element-383" data-mp="1m4pbj9ige6du">
          <motion.p initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.45
        }} className="mb-5 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-384" data-mp="1r04lvrh95k5i">
            The attention problem
          </motion.p>

          <motion.h2 initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.12,
          duration: 0.55
        }} className="text-[clamp(2.25rem,4.3vw,4.35rem)] font-extrabold uppercase leading-[0.96] tracking-[-0.045em] text-[var(--fg)]" data-id="element-385" data-mp="3rrn3eq6roeo7">
            HCPs don’t lack content.
            <span className="mt-2 block text-[var(--brand)]" data-id="element-386" data-mp="3903yuex3gsk2">They lack time.</span>
          </motion.h2>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3,
          duration: 0.5
        }} className="mt-7 border-l-2 border-[var(--brand)] pl-5" data-id="element-387" data-mp="33zg5vnpb21n7">
            <p className="text-sm font-bold uppercase tracking-[0.16em] text-[var(--muted)]" data-id="element-388" data-mp="y79gkf93sx30">
              So we created the Snak.
            </p>
            <p className="mt-2 text-[clamp(1.35rem,2vw,1.9rem)] font-bold leading-tight text-[var(--fg)]" data-id="element-389" data-mp="t9hrru3r9r31">
              A short story you participate in.
            </p>
          </motion.div>

          <motion.p initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.45,
          duration: 0.5
        }} className="mt-6 max-w-xl text-[clamp(0.9rem,1.15vw,1.05rem)] leading-relaxed text-[var(--muted)]" data-id="element-390" data-mp="15pqwp4bhja4y">
            A Snak turns complex content into a short, engaging, gamified journey.
            HCPs don’t just read or watch—they choose, predict, react, and discover.
            Every interaction creates insight.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6,
          duration: 0.5
        }} className="mt-6 space-y-2" data-id="element-391" data-mp="rppu9kfknund">
            {PAYOFFS.map((payoff, index) => <div key={payoff} className="flex items-center gap-3" data-id="element-392" data-mp="277014h3s287v" data-mpi={index}>
                <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[var(--brand)] text-[10px] font-bold text-white" data-id="element-393" data-mp="25qmbfv711d71">
                  {index + 1}
                </span>
                <p className="text-sm font-semibold text-[var(--fg)]" data-id="element-394" data-mp="2foijuf8kxu1g">{payoff}</p>
              </div>)}
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        x: 30
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.25,
        duration: 0.7
      }} className="relative mt-8 flex min-h-0 flex-1 items-center lg:col-span-7 lg:mt-0 lg:h-full" aria-label="Examples of the Snak experience" data-id="element-395" data-mp="2ao4amtv8se1u">
          <div className="absolute inset-x-8 inset-y-10 rounded-[2.5rem] bg-white shadow-sm" data-id="element-396" data-mp="2yh4a8k0pwsen" />
          <div className="relative z-10 grid w-full grid-cols-5 items-center gap-3 px-2 xl:gap-4" data-id="element-397" data-mp="2ti9rfi2r33uj">
            {PROOF_SCREENS.map((screen, index) => <motion.figure key={screen.label} initial={{
            opacity: 0,
            y: 26
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.45 + index * 0.1,
            duration: 0.55
          }} className={screen.offset} data-id="element-398" data-mp="33fe6kxy6hz1u" data-mpi={index}>
                <div className="relative w-full overflow-hidden rounded-[1.1rem] bg-white shadow-[0_18px_45px_rgba(23,32,51,0.16)]" style={{
              aspectRatio: `${CROP_WIDTH} / ${CROP_HEIGHT}`
            }} data-id="element-399" data-mp="3d2rus6au1yy1">
                  <img src={SCREENS_IMAGE} alt={screen.alt} className="absolute max-w-none" style={{
                width: `${SOURCE_WIDTH / CROP_WIDTH * 100}%`,
                height: `${SOURCE_HEIGHT / CROP_HEIGHT * 100}%`,
                left: `${-(screen.x / CROP_WIDTH) * 100}%`,
                top: `${-(screen.y / CROP_HEIGHT) * 100}%`
              }} data-id="element-400" data-mp="33a2l9gyuev5c" />
                </div>
                <figcaption className="mt-3 flex items-center justify-center gap-1.5 text-[0.62rem] font-bold uppercase tracking-[0.14em] text-[var(--muted)]" data-id="element-401" data-mp="uw5u5wd5q77k">
                  {screen.label}
                  {index < PROOF_SCREENS.length - 1 && <ArrowRightIcon className="h-3 w-3 text-[var(--brand)]" aria-hidden="true" data-id="element-402" data-mp="m4nhvwjc0bjr" />}
                </figcaption>
              </motion.figure>)}
          </div>
        </motion.div>
      </div>
    </InteriorSlide>;
}